import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CabinetSummary } from "services/cabinet.service";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "containers/settings/ui/buttonStyles";
import { Translate } from "./task-row";
import TasksSection from "./tasks-section";

type Queues = CabinetSummary["queues"];

interface QueueCard {
  key: string;
  title: string;
  count: number;
  /** Lines under the number: the first is the main one, the others are quieter. */
  lines: string[];
  /** Where the button leads. */
  path: string;
  /** Only to look at, nothing to do: a muted card with a "View" button. */
  muted?: boolean;
}

/** A queue the person has work in: it exists in the summary and holds something. */
function hasWork<Q extends { count: number }>(queue: Q | null): queue is Q {
  return queue !== null && queue.count > 0;
}

/**
 * The cards of the work queues, top to bottom. How it is read: each `if` is one
 * card, drawn only when its queue holds something; the order of the `if`s is the
 * order on the page. «Documents to review» and «Mine, waiting for others» are
 * not in the task number, so they sit last; the second one is only to look at,
 * so its card is muted.
 */
function cardsToDraw(queues: Queues, companyId: string, t: Translate): QueueCard[] {
  const {
    team,
    probation,
    invoices,
    ws,
    customerFinance,
    medicalQueue,
    assetRequests,
    review,
    sent,
  } = queues;
  const cards: QueueCard[] = [];
  const myWork = (tab: string) => `/app/${companyId}/my-work/${tab}`;

  if (hasWork(team)) {
    cards.push({
      key: "team",
      title: t("tasks_queue_team"),
      count: team.count,
      lines: [t("tasks_meta_team")],
      path: myWork("pending-signature?type=team"),
    });
  }
  if (hasWork(probation)) {
    cards.push({
      key: "probation",
      title: t("tasks_queue_probation"),
      count: probation.count,
      lines: [],
      path: myWork("probation-reviews"),
    });
  }
  if (hasWork(invoices)) {
    cards.push({
      key: "invoices",
      title: t("tasks_queue_invoices"),
      count: invoices.count,
      lines: invoices.oldestDays === null ? [] : [t("tasks_meta_invoices", { days: invoices.oldestDays })],
      path: myWork("invoice-approvals"),
    });
  }
  if (hasWork(ws)) {
    cards.push({
      key: "ws",
      title: t("tasks_queue_ws"),
      count: ws.count,
      lines: [],
      path: myWork("ws-approvals"),
    });
  }
  if (hasWork(customerFinance)) {
    cards.push({
      key: "customerFinance",
      title: t("tasks_queue_customer_finance"),
      count: customerFinance.count,
      lines: [],
      path: myWork("pending-approvals"),
    });
  }
  if (hasWork(medicalQueue)) {
    cards.push({
      key: "medicalQueue",
      title: t("tasks_queue_medical"),
      count: medicalQueue.count,
      lines: [],
      path: myWork("medical-checks"),
    });
  }
  if (hasWork(assetRequests)) {
    cards.push({
      key: "assetRequests",
      title: t("tasks_queue_asset_requests"),
      count: assetRequests.count,
      lines: [],
      path: `/app/${companyId}/assets/requests`,
    });
  }
  if (hasWork(review)) {
    cards.push({
      key: "review",
      title: t("tasks_queue_review"),
      count: review.count,
      lines: [t("tasks_meta_review", { date: review.oldestDate }), t("tasks_review_not_counted")],
      path: myWork("pending-files"),
    });
  }
  if (hasWork(sent)) {
    cards.push({
      key: "sent",
      title: t("tasks_queue_sent"),
      count: sent.count,
      lines: [t("tasks_meta_sent")],
      path: `/app/${companyId}/unsigned-files`,
      muted: true,
    });
  }
  return cards;
}

/** «Darbo eilės»: one card per queue the person works in, left out when there is none. */
export default function QueueCards({ queues, companyId }: { queues: Queues; companyId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cards = cardsToDraw(queues, companyId, t);
  if (cards.length === 0) return null;

  return (
    <TasksSection title={t("tasks_section_queues")}>
      <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => (
          <li
            key={card.key}
            className={`flex flex-col justify-between gap-4 rounded-lg border border-gray-200 p-4 ${
              card.muted ? "bg-gray-50" : "bg-white"
            }`}
          >
            <div>
              <h3 className="text-sm font-medium text-gray-900">{card.title}</h3>
              <p className="mt-1 text-3xl font-semibold tabular-nums text-gray-900">{card.count}</p>
              {card.lines.map((line, index) => (
                <p key={index} className={`mt-1 text-xs ${index === 0 ? "text-gray-600" : "text-gray-400"}`}>
                  {line}
                </p>
              ))}
            </div>
            <button
              type="button"
              className={`self-start ${card.muted ? SECONDARY_BUTTON : PRIMARY_BUTTON}`}
              onClick={() => navigate(card.path)}
              aria-label={`${card.muted ? t("tasks_action_view") : t("tasks_action_open")}: ${card.title}`}
            >
              {card.muted ? t("tasks_action_view") : t("tasks_action_open")}
            </button>
          </li>
        ))}
      </ul>
    </TasksSection>
  );
}
